// @flow
import React, { type Node } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { COLORS } from 'Components/utils/color/colors';
import { hasAtLeastOnePrice } from 'Components/utils/price/price.util';

import { Button, type ButtonProps } from 'Components/frameworks/button/Button.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Price, type PriceProps } from 'Components/frameworks/price/Price.jsx';

import { themes } from './PricingTable.themes';

export type PricingTableProps = {
  button?: ButtonProps,
  className?: string,
  icons?: Array<IconName>,
  infos?: Array<string | Node>,
  label?: string,
  noPriceLabel?: string,
  price?: PriceProps,
  theme?: string,
};

export function PricingTable(props: PricingTableProps) {
  const {
    button,
    className,
    icons,
    infos,
    label,
    noPriceLabel = '',
    price,
    theme = 'default',
  } = props;

  const {
    thButton,
    thHeader,
    thIconsList,
    thIconsListItem,
    thIconWidth,
    thInfosList,
    thInfosListItem,
    thLabel,
    thNoPriceLabel,
    thPriceTheme,
    thRoot,
  } = themes[theme];

  return (
    <section className={classnames(className, thRoot)}>
      <header className={thHeader}>
        {hasAtLeastOnePrice(price) ? (
          <Price {...price} theme={thPriceTheme} />
        ) : (
          <p data-testid="PricingTable-noPriceLabel" className={thNoPriceLabel}>
            {noPriceLabel}
          </p>
        )}
        {!!label && (
          <p data-testid="PricingTable-label" className={thLabel}>
            {label}
          </p>
        )}
      </header>
      {!isEmpty(icons) && (
        <ul className={thIconsList}>
          {icons.map(icon => (
            <li className={thIconsListItem} key={icon}>
              <Icon name={icon} width={thIconWidth} />
            </li>
          ))}
        </ul>
      )}
      {!isEmpty(infos) && (
        <ul className={thInfosList}>
          {infos.map(info => (
            <li data-testid="PricingTable-info" className={thInfosListItem} key={info}>
              {info}
            </li>
          ))}
        </ul>
      )}
      <Button
        className={thButton}
        colorBg={COLORS.BOOKING}
        colorBorder={COLORS.BOOKING}
        size="small"
        {...button}
      />
    </section>
  );
}
